import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import api from '../../api/client';
import { X, Building2, MapPin, Store, Radio, ArrowUpRight, Phone, Globe, Save, Landmark, Eye, ShieldAlert, Loader2, CheckCircle2, AlertCircle, XCircle, HelpCircle, ExternalLink, FileText, CheckSquare, Square, Maximize2, Minimize2, Compass, Crown, Copy, Share2 } from 'lucide-react';
import SARButton from './SARButton';

const streetViewUrl = (lat, lng) => `https://www.google.com/maps/@?api=1&map_action=pano&viewpoint=${lat},${lng}`;
const googleMapsUrl = (lat, lng) => `https://www.google.com/maps?q=${lat},${lng}`;


// -------- Compute panorama LOCALMENTE con edificios cacheados (fallback Overpass down) --------
function _haversine_m(lat1, lng1, lat2, lng2) {
  const R = 6371000;
  const p1 = lat1 * Math.PI / 180, p2 = lat2 * Math.PI / 180;
  const dp = (lat2 - lat1) * Math.PI / 180;
  const dl = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(dp / 2) ** 2 + Math.cos(p1) * Math.cos(p2) * Math.sin(dl / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
}
function _bearing_deg(lat1, lng1, lat2, lng2) {
  const p1 = lat1 * Math.PI / 180, p2 = lat2 * Math.PI / 180;
  const dl = (lng2 - lng1) * Math.PI / 180;
  const y = Math.sin(dl) * Math.cos(p2);
  const x = Math.cos(p1) * Math.sin(p2) - Math.sin(p1) * Math.cos(p2) * Math.cos(dl);
  return (Math.atan2(y, x) * 180 / Math.PI + 360) % 360;
}
function _compass(b) {
  const labels = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
  return labels[Math.floor(((b + 22.5) % 360) / 45)];
}
function _computeLocalPanorama(center, candidates, protectedList, sectorsCount = 36) {
  if (!center?.lat || !center?.lng) return null;
  const roof = center.height_m || 15;
  const N = sectorsCount;
  const sectorSize = 360 / N;
  const sectors = Array.from({ length: N }, (_, i) => ({
    bearing: +(i * sectorSize).toFixed(1),
    compass: _compass(i * sectorSize),
    obstacle_height_m: null, obstacle_distance_m: null, obstacle_name: null,
    obstacle_type: null, elevation_deg: null, is_obstacle: false,
  }));
  const pool = [...(candidates || []), ...(protectedList || [])];
  let seen = 0;
  for (const b of pool) {
    if (!b?.lat || !b?.lng || !b?.height_m) continue;
    if (b.lat === center.lat && b.lng === center.lng) continue;
    const d = _haversine_m(center.lat, center.lng, b.lat, b.lng);
    if (d < 5 || d > 500) continue;
    seen++;
    const br = _bearing_deg(center.lat, center.lng, b.lat, b.lng);
    const idx = Math.floor(br / sectorSize) % N;
    const elev = Math.atan2(b.height_m - roof, d) * 180 / Math.PI;
    const s = sectors[idx];
    if (s.elevation_deg === null || elev > s.elevation_deg) {
      s.obstacle_height_m = +b.height_m.toFixed(1);
      s.obstacle_distance_m = +d.toFixed(1);
      s.obstacle_name = b.name || null;
      s.obstacle_type = b.type || null;
      s.elevation_deg = +elev.toFixed(2);
      s.is_obstacle = elev > 0;
    }
  }
  if (seen === 0) return null;
  const obstructed = sectors.filter((s) => s.is_obstacle).length;
  return {
    roof_height_m: roof,
    radius_m: 500,
    sectors_total: N,
    sectors_analyzed: seen,
    obstructed_sectors: obstructed,
    clear_sectors: N - obstructed,
    coverage_pct: +(100 * (N - obstructed) / N).toFixed(1),
    sectors,
  };
}

const STATUS_META = {
  compatible:   { label: 'Compatible',    icon: CheckCircle2, color: 'text-[#A3FF12]', bg: 'bg-[#A3FF12]/10 border-[#A3FF12]/30' },
  posible:      { label: 'Posible',       icon: HelpCircle,   color: 'text-yellow-300', bg: 'bg-yellow-500/10 border-yellow-500/30' },
  afectado:     { label: 'Afectado',      icon: AlertCircle,  color: 'text-orange-300', bg: 'bg-orange-500/10 border-orange-500/30' },
  incompatible: { label: 'Incompatible',  icon: XCircle,      color: 'text-red-300',    bg: 'bg-red-500/10 border-red-500/30' },
  sin_datos:    { label: 'Sin datos',     icon: HelpCircle,   color: 'text-white/40',   bg: 'bg-white/5 border-white/10' },
};

const AreaAnalysisPanel = ({ open, onClose, data, areaName, onSaveSite }) => {
  const [tab, setTab] = useState('candidates');
  const [afecciones, setAfecciones] = useState(null);
  const [afLoading, setAfLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  // key = `${tab}:${idx}` — permite compartir el mismo índice entre pestañas.
  const [selected, setSelected] = useState(new Set());
  const [launchingSar, setLaunchingSar] = useState(false);
  const [onlyDominant, setOnlyDominant] = useState(false);
  const [panorama, setPanorama] = useState(null);  // { building, data, loading, sector, radius, sectors }

  const toggleSel = (key) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });
  const clearSel = () => setSelected(new Set());

  // Coordenada representativa para afecciones (centroide del área o primer candidato)
  const centerLat = data?.center_lat ?? data?.candidates?.[0]?.lat;
  const centerLng = data?.center_lng ?? data?.candidates?.[0]?.lng;

  const analyzeAfecciones = async () => {
    if (!centerLat || !centerLng) return;
    setAfLoading(true);
    try {
      const { data: res } = await api.post('/afecciones/analyze', {
        lat: centerLat, lng: centerLng, address: areaName || '',
      });
      setAfecciones(res);
    } catch (e) {
      alert('No se pudo analizar: ' + (e.response?.data?.detail || e.message));
    } finally { setAfLoading(false); }
  };

  // Reset al cambiar de área
  useEffect(() => { setAfecciones(null); setSelected(new Set()); setTab('candidates'); }, [areaName, centerLat, centerLng]);

  if (!open || !data) return null;

  const { stats = {}, streets = [], candidates: candidatesRaw = [], businesses = [], towers = [], protected: protectedBuildings = [] } = data || {};
  const candidates = onlyDominant ? candidatesRaw.filter((c) => c.is_dominant) : candidatesRaw;

  const openPanorama = async (c, opts = {}) => {
    const radius_m = opts.radius_m || 400;
    const sectors_n = opts.sectors || 36;
    setPanorama({ building: c, data: null, loading: true, error: null, radius: radius_m, sectors: sectors_n, focusSector: null });
    try {
      const r = await api.post('/research/panorama', {
        lat: c.lat, lng: c.lng, height_m: c.height_m || 15, radius_m, sectors: sectors_n,
      });
      setPanorama((p) => ({ ...(p || {}), building: c, data: r.data, loading: false, radius: radius_m, sectors: sectors_n }));
    } catch (e) {
      const local = _computeLocalPanorama(c, candidatesRaw, protectedBuildings, sectors_n);
      if (local) {
        setPanorama((p) => ({ ...(p || {}), building: c, data: { ...local, source: 'local' }, loading: false, radius: radius_m, sectors: sectors_n }));
      } else {
        setPanorama((p) => ({ ...(p || {}), building: c, data: null, loading: false,
          error: (e?.response?.data?.detail || e.message) + '. Sin datos locales suficientes.', radius: radius_m, sectors: sectors_n }));
      }
    }
  };

  // Elementos seleccionables por pestaña (los que tienen lat/lng)
  const SELECTABLE = {
    candidates: candidates,
    protected:  protectedBuildings,
    businesses: businesses,
    towers:     towers,
  };
  const selectableList = SELECTABLE[tab] || [];
  const selectedInTab = selectableList
    .map((it, i) => ({ it, key: `${tab}:${i}` }))
    .filter(({ key }) => selected.has(key));
  const allSelectedThisTab = selectableList.length > 0 && selectedInTab.length === selectableList.length;
  const toggleAllTab = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allSelectedThisTab) {
        selectableList.forEach((_, i) => next.delete(`${tab}:${i}`));
      } else {
        selectableList.forEach((_, i) => next.add(`${tab}:${i}`));
      }
      return next;
    });
  };

  const selectedItems = Array.from(selected)
    .map((k) => {
      const [t, idx] = k.split(':');
      const list = SELECTABLE[t] || [];
      return list[Number(idx)];
    })
    .filter((x) => x && x.lat && x.lng);

  // Launch a SAR async job per selected item AND create prospection when done
  const launchBulkSar = async () => {
    if (selectedItems.length === 0) return;
    if (selectedItems.length > 30) {
      if (!window.confirm(`Vas a lanzar ${selectedItems.length} SAR en paralelo. ¿Continuar?`)) return;
    }
    setLaunchingSar(true);
    // 1) Kick off async SAR jobs
    const jobs = [];
    for (const it of selectedItems) {
      const payload = {
        lat: it.lat, lng: it.lng,
        address: it.address ? `${it.address}${it.housenumber ? ' ' + it.housenumber : ''}` : (it.name || areaName || ''),
        height_m: it.height_m || null,
        site_name: it.name || `Candidato área ${areaName || ''}`.trim(),
      };
      if (afecciones) payload.afecciones_report = afecciones;
      try {
        const { data } = await api.post('/research/sar/async', payload);
        jobs.push({ job_id: data.job_id, payload });
      } catch { /* skip */ }
    }
    if (jobs.length === 0) {
      setLaunchingSar(false);
      toast.error('No se pudo lanzar ningún SAR');
      return;
    }
    toast.info(`${jobs.length} SAR lanzados · esperando resultados y creando prospecciones…`);
    // 2) Poll each job and create prospection when done. Limite total ~4 min.
    const start = Date.now();
    const pending = new Set(jobs.map((j) => j.job_id));
    let created = 0, failed = 0;
    while (pending.size > 0 && Date.now() - start < 240000) {
      await new Promise((r) => setTimeout(r, 3000));
      for (const j of jobs) {
        if (!pending.has(j.job_id)) continue;
        try {
          const { data: st } = await api.get(`/research/sar/jobs/${j.job_id}`);
          if (st.status === 'done' && st.sar_id) {
            // Crea la prospección enlazando el SAR
            try {
              await api.post('/prospecciones/from-sar', {
                sar_id: st.sar_id,
                address: j.payload.address,
                lat: j.payload.lat, lng: j.payload.lng,
                height_m: j.payload.height_m,
              });
              created++;
            } catch { failed++; }
            pending.delete(j.job_id);
          } else if (st.status === 'error') {
            failed++;
            pending.delete(j.job_id);
          }
        } catch { /* keep polling */ }
      }
    }
    setLaunchingSar(false);
    clearSel();
    if (created > 0) toast.success(`${created} SAR generados y creados como prospecciones. Ábrelas en /app/prospecciones`);
    if (failed > 0) toast.error(`${failed} fallaron`);
    if (pending.size > 0) toast.warning(`${pending.size} siguen procesando en background`);
  };

  const saveSelectedAsSites = async () => {
    if (selectedItems.length === 0) return;
    let ok = 0;
    for (const it of selectedItems) {
      try {
        await onSaveSite(it, afecciones);
        ok++;
      } catch { /* onSaveSite ya muestra su propio error */ }
    }
    clearSel();
    if (ok > 0) toast.success(`${ok} sites guardados`);
  };

  const tabs = [
    { id: 'candidates', label: `Candidatos`, count: candidates.length, icon: Building2 },
    { id: 'protected', label: `Protegidos`, count: protectedBuildings.length, icon: Landmark, danger: true },
    { id: 'afecciones', label: 'Afecciones', count: afecciones?.results?.length || '', icon: ShieldAlert, danger: false, accent: true },
    { id: 'streets', label: 'Calles', count: streets.length, icon: MapPin },
    { id: 'businesses', label: 'Negocios', count: businesses.length, icon: Store },
    { id: 'towers', label: 'Antenas', count: towers.length, icon: Radio },
  ];

  return (
    <div className={`fixed inset-x-0 bottom-0 z-50 transition-[height,max-height] duration-200 lg:max-w-6xl lg:left-1/2 lg:right-auto lg:-translate-x-1/2 lg:inset-x-auto ${
      expanded ? 'h-[92vh] lg:h-[90vh]' : 'h-[65vh] lg:h-[70vh]'
    }`} data-testid="area-analysis-panel-wrapper">
      {/* Handle bar (drag / tap para expandir/contraer) */}
      <button onClick={() => setExpanded((v) => !v)} data-testid="btn-expand-panel"
        className="absolute -top-3 left-1/2 -translate-x-1/2 z-10 w-16 h-6 flex items-center justify-center bg-[#0a0a0a] border border-white/20 rounded-full hover:bg-white/10 shadow-lg"
        title={expanded ? 'Contraer' : 'Expandir'}>
        <div className="w-8 h-1 rounded-full bg-white/40"/>
      </button>
      <div className="bg-[#0a0a0a] border-t border-white/10 lg:border lg:rounded-2xl w-full h-full flex flex-col shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="area-analysis-panel">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 px-5 sm:px-6 py-3 sm:py-4 border-b border-white/10">
          <div className="min-w-0 flex-1">
            <div className="text-xs font-bold tracking-widest text-[#A3FF12]">ANÁLISIS DEL ÁREA</div>
            <h2 className="text-lg sm:text-xl font-black text-white mt-0.5 truncate">{areaName || 'Zona KMZ'}</h2>
            <p className="text-white/50 text-xs mt-1">{stats.total_buildings ?? 0} edificios · {stats.total_streets ?? 0} calles · {stats.total_businesses ?? 0} negocios · {stats.existing_towers ?? 0} antenas</p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {centerLat && centerLng && (
              <SARButton lat={centerLat} lng={centerLng} address={areaName}
                afecciones_report={afecciones}
                variant="primary"/>
            )}
            <button onClick={() => setExpanded((v) => !v)} data-testid="btn-toggle-expand"
              className="text-white/50 hover:text-white p-1.5" title={expanded ? 'Contraer panel' : 'Expandir panel'}>
              {expanded ? <Minimize2 size={20}/> : <Maximize2 size={20}/>}
            </button>
            <button onClick={onClose} data-testid="close-analysis-panel"
              className="text-white/50 hover:text-white p-1.5" title="Cerrar (Esc)"><X size={22}/></button>
          </div>
        </div>

        {/* Stats strip */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 sm:gap-3 px-5 sm:px-6 py-4 border-b border-white/10">
          <div className="bg-black/40 border border-white/5 rounded-lg p-2 sm:p-3">
            <div className="text-white/50 text-[10px] sm:text-xs">Edificios</div>
            <div className="text-[#A3FF12] text-lg sm:text-2xl font-black">{stats.total_buildings ?? 0}</div>
          </div>
          <div className="bg-black/40 border border-white/5 rounded-lg p-2 sm:p-3">
            <div className="text-white/50 text-[10px] sm:text-xs">Altura media</div>
            <div className="text-[#A3FF12] text-lg sm:text-2xl font-black">{stats.avg_height ? `${stats.avg_height}m` : '–'}</div>
          </div>
          <div className="bg-black/40 border border-white/5 rounded-lg p-2 sm:p-3">
            <div className="text-white/50 text-[10px] sm:text-xs">Altura máx</div>
            <div className="text-[#A3FF12] text-lg sm:text-2xl font-black">{stats.max_height ? `${stats.max_height}m` : '–'}</div>
          </div>
          <div className="bg-black/40 border border-purple-500/30 rounded-lg p-2 sm:p-3">
            <div className="text-purple-300 text-[10px] sm:text-xs">Protegidos</div>
            <div className={`text-lg sm:text-2xl font-black ${(stats.protected_count ?? 0) > 0 ? 'text-purple-400' : 'text-white/50'}`}>{stats.protected_count ?? 0}</div>
          </div>
          <div className="bg-black/40 border border-white/5 rounded-lg p-2 sm:p-3 col-span-2 sm:col-span-1">
            <div className="text-white/50 text-[10px] sm:text-xs">Antenas</div>
            <div className={`text-lg sm:text-2xl font-black ${(stats.existing_towers ?? 0) === 0 ? 'text-red-400' : 'text-white'}`}>{stats.existing_towers ?? 0}</div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-white/10 px-3 sm:px-6 overflow-x-auto">
          {tabs.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 px-3 sm:px-4 py-3 text-xs sm:text-sm font-bold whitespace-nowrap border-b-2 transition-colors ${
                tab === t.id
                  ? (t.danger ? 'text-purple-300 border-purple-500' : 'text-[#A3FF12] border-[#A3FF12]')
                  : (t.danger && t.count > 0 ? 'text-purple-400 border-transparent hover:text-purple-300' : 'text-white/50 border-transparent hover:text-white/80')
              }`}>
              <t.icon size={16}/> {t.label}
              <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                tab === t.id
                  ? (t.danger ? 'bg-purple-500/20' : 'bg-[#A3FF12]/20')
                  : (t.danger && t.count > 0 ? 'bg-purple-500/20' : 'bg-white/10')
              }`}>{t.count}</span>
            </button>
          ))}
        </div>

        {/* Selection action bar (aparece al seleccionar al menos 1) */}
        {selectableList.length > 0 && (
          <div className="sticky top-0 z-10 bg-black/85 backdrop-blur border-b border-white/10 px-5 sm:px-6 py-2 flex items-center gap-2 flex-wrap"
            data-testid="selection-bar">
            <button onClick={toggleAllTab} data-testid="btn-select-all-tab"
              className="flex items-center gap-1.5 bg-white/10 hover:bg-white/20 text-white text-[11px] font-bold px-2.5 py-1 rounded-lg">
              {allSelectedThisTab ? <CheckSquare size={12}/> : <Square size={12}/>}
              {allSelectedThisTab ? 'Deseleccionar' : `Todo (${selectableList.length})`}
            </button>
            <span className="text-white/50 text-[11px]">
              {selected.size > 0 ? <b className="text-white">{selected.size} seleccionado{selected.size !== 1 ? 's' : ''}</b> : 'Marca lo que quieras generar como SAR o guardar como site'}
            </span>
            {selected.size > 0 && (
              <div className="ml-auto flex items-center gap-2">
                <button onClick={clearSel} data-testid="btn-clear-sel"
                  className="text-white/60 hover:text-white text-[11px] font-bold px-2 py-1">
                  Limpiar
                </button>
                <button onClick={saveSelectedAsSites}
                  data-testid="btn-bulk-save-site"
                  className="flex items-center gap-1 bg-white/10 hover:bg-white/20 text-white text-[11px] font-black px-3 py-1.5 rounded-lg">
                  <Save size={12}/> Guardar como sites
                </button>
                <button onClick={launchBulkSar} disabled={launchingSar}
                  data-testid="btn-bulk-launch-sar"
                  className="flex items-center gap-1 bg-[#A3FF12] hover:bg-[#b8ff3a] text-black text-[11px] font-black px-3 py-1.5 rounded-lg disabled:opacity-40">
                  {launchingSar ? <Loader2 className="animate-spin" size={12}/> : <FileText size={12}/>}
                  Generar SAR ({selectedItems.length})
                </button>
              </div>
            )}
          </div>
        )}

        {/* Tab content */}
        <div className="flex-1 overflow-y-auto px-3 sm:px-6 py-4">
          {tab === 'protected' && (
            <div className="space-y-2">
              {protectedBuildings.length === 0 ? (
                <div className="bg-[#A3FF12]/10 border border-[#A3FF12]/30 text-[#A3FF12] rounded-xl p-4 text-sm">
                  ✓ <strong>No hay edificios protegidos registrados</strong> en esta zona. Reduce el riesgo legal para la SAR.
                </div>
              ) : (
                <div className="bg-purple-500/10 border border-purple-500/30 text-purple-200 rounded-xl p-3 text-sm mb-2">
                  ⚠ <strong>{protectedBuildings.length} elementos protegidos</strong> detectados (BIC, catálogo histórico, patrimonio). Estos edificios <strong>no son candidatos</strong> y obligan a Licencia (no DR) en sus entornos.
                </div>
              )}
              {protectedBuildings.map((p, i) => {
                const k = `protected:${i}`;
                const sel = selected.has(k);
                return (
                <div key={p.id || i}
                  onClick={(e) => { if (!p.lat || !p.lng) return; if (e.target.closest('a,button,input')) return; toggleSel(k); }}
                  data-testid={`row-protected-${i}`}
                  className={`flex items-center gap-3 border rounded-xl p-3 ${p.lat && p.lng ? 'cursor-pointer' : ''} ${sel ? 'bg-purple-500/15 border-purple-400' : 'bg-purple-500/5 border-purple-500/20'}`}>
                  {p.lat && p.lng && (
                    <input type="checkbox" checked={sel} onChange={() => toggleSel(k)}
                      data-testid={`chk-protected-${i}`}
                      className="w-4 h-4 accent-purple-400 flex-shrink-0"/>
                  )}
                  <Landmark className="text-purple-400 flex-shrink-0" size={20}/>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-white font-bold truncate">{p.name}</span>
                      {p.kind && <span className="text-[10px] uppercase font-bold text-purple-300 bg-purple-500/20 px-2 py-0.5 rounded-full">{p.kind}</span>}
                      {p.ref_bic && <span className="text-[10px] uppercase font-bold text-purple-200 bg-purple-500/30 px-2 py-0.5 rounded-full">BIC {p.ref_bic}</span>}
                    </div>
                    <div className="text-purple-200/60 text-xs flex items-center gap-3 mt-1 flex-wrap">
                      {p.operator && <span>{p.operator}</span>}
                      <span>{p.lat?.toFixed(5)}, {p.lng?.toFixed(5)}</span>
                    </div>
                  </div>
                  {p.wikidata && <a href={`https://www.wikidata.org/wiki/${p.wikidata}`} target="_blank" rel="noreferrer" className="text-purple-400/60 hover:text-purple-300 p-2" title="Wikidata"><ArrowUpRight size={14}/></a>}
                  {p.lat && p.lng && (
                    <a href={streetViewUrl(p.lat, p.lng)} target="_blank" rel="noreferrer" className="text-purple-400/60 hover:text-purple-300 p-2" title="Street View">
                      <Eye size={14}/>
                    </a>
                  )}
                </div>
                );
              })}
            </div>
          )}

          {tab === 'candidates' && (
            <div className="space-y-2">
              <div className="flex items-center justify-between gap-3 pb-2 border-b border-white/5 flex-wrap">
                <label className="flex items-center gap-2 text-xs text-white/70 cursor-pointer" data-testid="toggle-dominant">
                  <input type="checkbox" checked={onlyDominant} onChange={(e) => setOnlyDominant(e.target.checked)}
                    className="w-4 h-4 accent-[#A3FF12]"/>
                  <Crown size={14} className="text-yellow-300"/>
                  <span>Solo edificios dominantes ({candidatesRaw.filter((c) => c.is_dominant).length}/{candidatesRaw.length})</span>
                </label>
                <span className="text-white/40 text-[10px]">Dominante = altura ≥ media vecinos+3m en 100m</span>
              </div>
              {candidates.length === 0 && <p className="text-white/40 text-sm py-4">{onlyDominant ? 'Ningún edificio dominante en este área.' : 'No se han encontrado edificios con altura conocida en esta área.'}</p>}
              {candidates.map((c, i) => {
                const k = `candidates:${i}`;
                const sel = selected.has(k);
                return (
                <div key={c.id || i}
                  onClick={(e) => { if (e.target.closest('a,button,input')) return; toggleSel(k); }}
                  data-testid={`row-candidate-${i}`}
                  className={`flex items-center gap-3 border rounded-xl p-3 transition-colors cursor-pointer ${sel ? 'bg-[#A3FF12]/15 border-[#A3FF12]/60' : 'bg-black/30 border-white/5 hover:border-[#A3FF12]/30'}`}>
                  <input type="checkbox" checked={sel} onChange={() => toggleSel(k)}
                    data-testid={`chk-candidate-${i}`}
                    className="w-4 h-4 accent-[#A3FF12] flex-shrink-0"/>
                  <div className="bg-[#A3FF12]/10 text-[#A3FF12] w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 font-black text-sm">
                    #{i+1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-white font-bold truncate">{c.name || `Edificio ${c.type || 'sin nombre'}`}</span>
                      {c.type && <span className="text-[10px] uppercase font-bold text-white/50 bg-white/5 px-2 py-0.5 rounded-full">{c.type}</span>}
                      {c.is_dominant && (
                        <span className="text-[10px] uppercase font-black bg-yellow-500/20 text-yellow-300 border border-yellow-500/40 px-2 py-0.5 rounded-full flex items-center gap-1" title={`+${c.dominance_delta_m ?? '?'}m sobre vecinos`}>
                          <Crown size={10}/> DOMINANTE
                        </span>
                      )}
                    </div>
                    <div className="text-white/40 text-xs truncate mt-0.5">
                      {c.address ? `${c.address}${c.housenumber ? ' ' + c.housenumber : ''}` : 'Sin dirección OSM'}
                    </div>
                    <div className="text-white/60 text-xs mt-1 flex items-center gap-3 flex-wrap">
                      <span className="text-[#A3FF12] font-black">{c.height_m}m</span>
                      {c.levels && <span>{c.levels} plantas</span>}
                      <span className="text-white/30">{c.lat?.toFixed(5)}, {c.lng?.toFixed(5)}</span>
                    </div>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-1 flex-shrink-0">
                    <button onClick={() => onSaveSite(c, afecciones)}
                      className="p-2 text-[#A3FF12] hover:bg-[#A3FF12]/10 rounded-lg"
                      title={afecciones ? 'Guardar con informe de afecciones adjunto' : 'Guardar como emplazamiento'}>
                      <Save size={16}/>
                    </button>
                    <button onClick={() => openPanorama(c)}
                      className="p-2 text-blue-300 hover:bg-blue-500/10 rounded-lg"
                      data-testid={`btn-360-${i}`}
                      title="Vista panorámica 360° desde azotea">
                      <Compass size={16}/>
                    </button>
                    <a href={streetViewUrl(c.lat, c.lng)} target="_blank" rel="noreferrer"
                      className="p-2 text-white/40 hover:text-[#A3FF12]" title="Street View">
                      <Eye size={16}/>
                    </a>
                    <a href={googleMapsUrl(c.lat, c.lng)} target="_blank" rel="noreferrer"
                      className="p-2 text-white/40 hover:text-[#A3FF12]" title="Ver en Google Maps">
                      <ArrowUpRight size={16}/>
                    </a>
                  </div>
                </div>
                );
              })}
            </div>
          )}

          {tab === 'streets' && (
            <div className="grid sm:grid-cols-2 gap-2">
              {streets.length === 0 && <p className="text-white/40 text-sm py-4">No se han encontrado calles.</p>}
              {streets.map((s, i) => (
                <div key={i} className="flex items-center gap-3 bg-black/30 border border-white/5 rounded-lg px-3 py-2">
                  <MapPin size={14} className="text-[#A3FF12] flex-shrink-0"/>
                  <span className="text-white font-semibold truncate flex-1">{s.name}</span>
                  <span className="text-white/40 text-[10px] uppercase">{s.type}</span>
                </div>
              ))}
            </div>
          )}

          {tab === 'businesses' && (
            <div className="space-y-2">
              {businesses.length === 0 && <p className="text-white/40 text-sm py-4">No se han encontrado negocios.</p>}
              {businesses.map((b, i) => {
                const k = `businesses:${i}`;
                const sel = selected.has(k);
                return (
                <div key={b.id || i}
                  onClick={(e) => { if (!b.lat || !b.lng) return; if (e.target.closest('a,button,input')) return; toggleSel(k); }}
                  data-testid={`row-business-${i}`}
                  className={`flex items-center gap-3 border rounded-lg px-3 py-2.5 ${b.lat && b.lng ? 'cursor-pointer' : ''} ${sel ? 'bg-[#A3FF12]/15 border-[#A3FF12]/50' : 'bg-black/30 border-white/5'}`}>
                  {b.lat && b.lng && (
                    <input type="checkbox" checked={sel} onChange={() => toggleSel(k)}
                      data-testid={`chk-business-${i}`}
                      className="w-4 h-4 accent-[#A3FF12] flex-shrink-0"/>
                  )}
                  <Store size={14} className="text-[#A3FF12] flex-shrink-0"/>
                  <div className="flex-1 min-w-0">
                    <div className="text-white font-semibold truncate text-sm">{b.name}</div>
                    <div className="text-white/40 text-xs flex items-center gap-3 flex-wrap">
                      <span className="uppercase">{b.kind}</span>
                      {b.address && <span>{b.address}{b.housenumber ? ' '+b.housenumber : ''}</span>}
                    </div>
                  </div>
                  {b.phone && <a href={`tel:${b.phone}`} className="p-1.5 text-white/40 hover:text-[#A3FF12]" title="Llamar"><Phone size={14}/></a>}
                  {b.website && <a href={b.website} target="_blank" rel="noreferrer" className="p-1.5 text-white/40 hover:text-[#A3FF12]" title="Web"><Globe size={14}/></a>}
                  {b.lat && b.lng && (
                    <a href={streetViewUrl(b.lat, b.lng)} target="_blank" rel="noreferrer" className="p-1.5 text-white/40 hover:text-[#A3FF12]" title="Street View"><Eye size={14}/></a>
                  )}
                </div>
                );
              })}
            </div>
          )}

          {tab === 'towers' && (
            <div className="space-y-2">
              {towers.length === 0 && (
                <div className="bg-red-500/10 border border-red-500/30 text-red-300 rounded-xl p-4 text-sm">
                  <strong>⚡ Sin antenas registradas en esta zona.</strong> Oportunidad clara para ofrecer infraestructura nueva.
                </div>
              )}
              {towers.map((t, i) => {
                const k = `towers:${i}`;
                const sel = selected.has(k);
                return (
                <div key={t.id || i}
                  onClick={(e) => { if (!t.lat || !t.lng) return; if (e.target.closest('a,button,input')) return; toggleSel(k); }}
                  data-testid={`row-tower-${i}`}
                  className={`flex items-center gap-3 border rounded-lg px-3 py-2.5 ${t.lat && t.lng ? 'cursor-pointer' : ''} ${sel ? 'bg-[#A3FF12]/15 border-[#A3FF12]/50' : 'bg-black/30 border-white/5'}`}>
                  {t.lat && t.lng && (
                    <input type="checkbox" checked={sel} onChange={() => toggleSel(k)}
                      data-testid={`chk-tower-${i}`}
                      className="w-4 h-4 accent-[#A3FF12] flex-shrink-0"/>
                  )}
                  <Radio size={14} className="text-orange-400 flex-shrink-0"/>
                  <div className="flex-1 min-w-0">
                    <div className="text-white font-semibold text-sm">Torre {t.type || 'telecom'}</div>
                    <div className="text-white/40 text-xs flex items-center gap-3 flex-wrap">
                      {t.operator && <span>{t.operator}</span>}
                      {t.height_m && <span>{t.height_m}m</span>}
                      <span>{t.lat?.toFixed(4)}, {t.lng?.toFixed(4)}</span>
                    </div>
                  </div>
                  {t.lat && t.lng && (
                    <a href={streetViewUrl(t.lat, t.lng)} target="_blank" rel="noreferrer" className="p-1.5 text-white/40 hover:text-[#A3FF12]" title="Street View"><Eye size={14}/></a>
                  )}
                </div>
                );
              })}
            </div>
          )}

          {tab === 'afecciones' && (
            <div className="space-y-3" data-testid="afecciones-tab-content">
              <div className="bg-black/40 border border-white/10 rounded-xl p-4 flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="text-white font-black flex items-center gap-2"><ShieldAlert size={16} className="text-[#A3FF12]"/> Afecciones legales</div>
                  <div className="text-white/50 text-xs mt-1">Análisis Adquiriam de las 10 afecciones urbanísticas típicas para telecomunicaciones (AESA, vías pecuarias, montes, Red Natura, edificios protegidos, etc.).</div>
                  {centerLat && centerLng && (
                    <div className="text-white/30 text-[10px] mt-1">Coord ref: {centerLat.toFixed(5)}, {centerLng.toFixed(5)}</div>
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <button onClick={analyzeAfecciones} disabled={afLoading || !centerLat}
                    data-testid="btn-analyze-afecciones"
                    className="flex items-center gap-2 bg-[#A3FF12] text-black font-black px-4 py-2 rounded-xl hover:bg-[#b8ff3a] disabled:opacity-40 text-sm">
                    {afLoading ? <Loader2 className="animate-spin" size={16}/> : <ShieldAlert size={16}/>}
                    {afecciones ? 'Volver a analizar' : 'Analizar con Adquiriam'}
                  </button>
                  {centerLat && centerLng && (
                    <SARButton lat={centerLat} lng={centerLng} address={areaName}
                      afecciones_report={afecciones}
                      variant="secondary"/>
                  )}
                </div>
              </div>

              {afecciones?.summary && (
                <div className="bg-[#A3FF12]/5 border border-[#A3FF12]/30 text-white/80 rounded-xl p-3 text-sm">
                  <b className="text-[#A3FF12]">Resumen:</b> {afecciones.summary}
                </div>
              )}

              {afecciones?.results?.map((r) => {
                const meta = STATUS_META[r.status] || STATUS_META.sin_datos;
                const Icon = meta.icon;
                return (
                  <div key={r.id} className={`rounded-xl border p-3 ${meta.bg}`} data-testid={`af-${r.id}`}>
                    <div className="flex items-start gap-3">
                      <Icon className={`${meta.color} flex-shrink-0 mt-0.5`} size={18}/>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-white font-black text-sm">{r.name}</span>
                          <span className={`text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full ${meta.color} bg-black/30`}>
                            {meta.label}
                          </span>
                        </div>
                        {r.details && <div className="text-white/70 text-xs mt-1.5 leading-relaxed">{r.details}</div>}
                        {r.source && <div className="text-white/40 text-[10px] mt-1"><i>{r.source}</i></div>}
                      </div>
                      {r.url && (
                        <a href={r.url} target="_blank" rel="noreferrer" className="text-white/40 hover:text-[#A3FF12] p-1.5 flex-shrink-0" title="Consulta oficial">
                          <ExternalLink size={14}/>
                        </a>
                      )}
                    </div>
                  </div>
                );
              })}

              {!afecciones && !afLoading && (
                <div className="text-white/40 text-sm py-6 text-center border border-dashed border-white/10 rounded-xl">
                  Pulsa <b>«Analizar con Adquiriam»</b> para revisar las 10 afecciones legales sobre esta ubicación.
                </div>
              )}
              {afLoading && (
                <div className="text-white/50 text-sm py-6 text-center flex items-center justify-center gap-2">
                  <Loader2 className="animate-spin" size={16}/> Adquiriam está revisando la normativa aplicable…
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      {panorama && <PanoramaModal state={panorama} onOpen={openPanorama} onClose={() => setPanorama(null)}/>}
    </div>
  );
};


// -------- PanoramaModal — radar polar de obstáculos por dirección --------
const PanoramaModal = ({ state, onOpen, onClose }) => {
  const { building, data, loading, error, radius = 400, sectors: nSectors = 36, focusSector } = state;
  const size = 380;
  const cx = size / 2, cy = size / 2, R = size / 2 - 20;
  const sectors = data?.sectors || [];
  const [selected, setSelected] = useState(focusSector ?? null);

  useEffect(() => { setSelected(null); /* nuevo edificio */ }, [building?.lat, building?.lng]);

  // Escala de elevación → longitud del rayo (0..R). 45° = R completo.
  const maxElev = 60;
  const rayLen = (deg) => {
    if (deg === null || deg === undefined) return R * 0.12;
    const clamped = Math.max(-20, Math.min(maxElev, deg));
    return R * (0.15 + 0.85 * ((clamped + 20) / (maxElev + 20)));
  };
  const colorFor = (s) => {
    if (s.elevation_deg === null || s.elevation_deg === undefined) return '#333';
    if (s.elevation_deg > 15) return '#f87171';
    if (s.elevation_deg > 0) return '#fbbf24';
    return '#A3FF12';
  };

  // Street View embed sin API key: layer=c + coordinates + bearing (cbp)
  const streetViewEmbed = (lat, lng, heading) => (
    `https://maps.google.com/maps?q=&layer=c&cbll=${lat},${lng}&cbp=11,${heading},0,0,0&output=svembed`
  );
  const streetViewOpen = (lat, lng, heading) => (
    `https://www.google.com/maps/@?api=1&map_action=pano&viewpoint=${lat},${lng}&heading=${heading}&pitch=10`
  );

  const activeSector = selected !== null ? sectors[selected] : null;
  const [ivInteractive, setIvInteractive] = useState(false);
  // Reset "clic para navegar" al cambiar de sector o edificio.
  useEffect(() => { setIvInteractive(false); }, [selected, building?.lat, building?.lng]);

  return (
    <div className="fixed inset-0 z-[70] bg-black/85 backdrop-blur-sm flex items-center justify-center p-3 sm:p-6" onClick={onClose} data-testid="panorama-modal">
      <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl w-full max-w-6xl max-h-[95vh] overflow-y-auto overscroll-contain shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="panorama-scroll">
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-white/10 sticky top-0 bg-[#0a0a0a] z-10">
          <div className="min-w-0 flex-1">
            <div className="text-xs font-black tracking-widest text-blue-300">VISTA PANORÁMICA · 360° DESDE AZOTEA</div>
            <h2 className="text-lg sm:text-xl font-black text-white mt-0.5 truncate">{building?.name || `Edificio (${building?.height_m}m)`}</h2>
            <p className="text-white/50 text-xs mt-0.5">Pulsa un sector del radar para ver la panorámica real de Google Street View.</p>
            {data?.source === 'local' && (
              <div className="text-yellow-300 text-[10px] mt-1 inline-block bg-yellow-500/10 border border-yellow-500/30 rounded px-2 py-0.5">
                ⚠ Overpass no disponible — vista aproximada con edificios ya cacheados del área
              </div>
            )}
          </div>
          <button onClick={onClose} className="text-white/50 hover:text-white p-1.5"><X size={22}/></button>
        </div>

        {/* Sliders radio/sectores */}
        <div className="flex items-center gap-4 px-5 py-3 border-b border-white/5 bg-black/30 flex-wrap text-xs">
          <label className="flex items-center gap-2 text-white/70">
            <span>Radio</span>
            <input type="range" min="100" max="1000" step="50" value={radius}
              onChange={(e) => onOpen(building, { radius_m: Number(e.target.value), sectors: nSectors })}
              data-testid="slider-radius"
              className="w-32 accent-[#A3FF12]"/>
            <span className="text-[#A3FF12] font-bold w-14">{radius}m</span>
          </label>
          <label className="flex items-center gap-2 text-white/70">
            <span>Sectores</span>
            <select value={nSectors} onChange={(e) => onOpen(building, { radius_m: radius, sectors: Number(e.target.value) })}
              data-testid="select-sectors"
              className="bg-black border border-white/10 rounded px-2 py-1 text-white">
              <option value={24}>24 (15°)</option>
              <option value={36}>36 (10°)</option>
              <option value={72}>72 (5°)</option>
            </select>
          </label>
          {data && (
            <div className="ml-auto flex flex-wrap items-center gap-3 text-white/50">
              <span>Sectores libres: <b className="text-[#A3FF12]">{data.clear_sectors}/{data.sectors_total}</b></span>
              <span>Cobertura: <b className={data.coverage_pct >= 70 ? 'text-[#A3FF12]' : data.coverage_pct >= 40 ? 'text-yellow-300' : 'text-red-400'}>{data.coverage_pct}%</b></span>
            </div>
          )}
        </div>

        {loading && (
          <div className="p-10 flex items-center justify-center gap-3 text-white/60">
            <Loader2 className="animate-spin text-blue-400" size={22}/>
            Consultando OpenStreetMap · {nSectors} sectores · radio {radius}m…
          </div>
        )}
        {error && (
          <div className="p-8 text-center">
            <div className="text-red-300 font-bold mb-2">No se pudo generar la vista</div>
            <div className="text-white/50 text-sm">{error}</div>
          </div>
        )}
        {data && (
          <div className="p-4 sm:p-6 grid lg:grid-cols-2 gap-4">
            {/* Radar polar SVG */}
            <div className="bg-black/40 border border-white/10 rounded-2xl p-3">
              <div className="text-white/60 text-xs mb-2 text-center">Pulsa un sector para ver la panorámica →</div>
              <svg viewBox={`0 0 ${size} ${size}`} className="w-full max-w-[420px] mx-auto block" data-testid="panorama-radar">
                {[0.25, 0.5, 0.75, 1].map((f) => (
                  <circle key={f} cx={cx} cy={cy} r={R * f} fill="none" stroke="#222" strokeDasharray="2 3"/>
                ))}
                {['N', 'E', 'S', 'W'].map((lbl, i) => {
                  const a = (i * 90 - 90) * Math.PI / 180;
                  const x = cx + Math.cos(a) * (R + 10);
                  const y = cy + Math.sin(a) * (R + 10);
                  return (
                    <g key={lbl}>
                      <line x1={cx} y1={cy} x2={cx + Math.cos(a) * R} y2={cy + Math.sin(a) * R} stroke="#333"/>
                      <text x={x} y={y + 4} fill="#888" fontSize="12" fontWeight="bold" textAnchor="middle">{lbl}</text>
                    </g>
                  );
                })}
                {sectors.map((s, i) => {
                  const a = (s.bearing - 90) * Math.PI / 180;
                  const len = rayLen(s.elevation_deg);
                  const arcOuterA = a - Math.PI / sectors.length;
                  const arcOuterB = a + Math.PI / sectors.length;
                  const oax = cx + Math.cos(arcOuterA) * len;
                  const oay = cy + Math.sin(arcOuterA) * len;
                  const obx = cx + Math.cos(arcOuterB) * len;
                  const oby = cy + Math.sin(arcOuterB) * len;
                  const isSel = selected === i;
                  return (
                    <g key={i} onClick={() => setSelected(i)} style={{ cursor: 'pointer' }}
                       data-testid={`sector-${i}`}>
                      <polygon points={`${cx},${cy} ${oax},${oay} ${obx},${oby}`}
                        fill={colorFor(s)}
                        fillOpacity={isSel ? 0.9 : (s.is_obstacle ? 0.55 : 0.25)}
                        stroke={isSel ? '#fff' : colorFor(s)}
                        strokeWidth={isSel ? 2 : 0.5}>
                        <title>{`${s.compass} (${s.bearing}°) — ${s.obstacle_height_m ? `${s.obstacle_height_m}m @ ${s.obstacle_distance_m}m` : 'sin obstáculo'} · elev ${s.elevation_deg ?? '–'}°`}</title>
                      </polygon>
                    </g>
                  );
                })}
                <circle cx={cx} cy={cy} r="5" fill="#3b82f6"/>
                <circle cx={cx} cy={cy} r="10" fill="none" stroke="#3b82f6" strokeOpacity="0.4"/>
              </svg>
              <div className="flex flex-wrap gap-3 text-[11px] mt-3 justify-center">
                <span className="flex items-center gap-1.5"><span className="w-3 h-3 bg-[#A3FF12] rounded-sm"/>Vía libre</span>
                <span className="flex items-center gap-1.5"><span className="w-3 h-3 bg-yellow-400 rounded-sm"/>Obstáculo leve</span>
                <span className="flex items-center gap-1.5"><span className="w-3 h-3 bg-red-400 rounded-sm"/>Grave &gt;15°</span>
              </div>
            </div>

            {/* Vista Google Street View del sector seleccionado */}
            <div className="bg-black/40 border border-white/10 rounded-2xl overflow-hidden flex flex-col min-h-[400px]">
              {activeSector ? (
                <>
                  <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between flex-wrap gap-2">
                    <div>
                      <div className="text-blue-300 text-[10px] font-black uppercase">Vista real Street View</div>
                      <div className="text-white font-bold text-sm">
                        {activeSector.compass} · {activeSector.bearing}°
                        {activeSector.obstacle_name && <span className="text-white/60"> — {activeSector.obstacle_name}</span>}
                      </div>
                      {activeSector.obstacle_height_m && (
                        <div className="text-white/50 text-xs mt-0.5">
                          Edificio a {activeSector.obstacle_distance_m}m · altura {activeSector.obstacle_height_m}m · elevación aparente {activeSector.elevation_deg}°
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                    <button
                      onClick={async () => {
                        const url = streetViewOpen(building.lat, building.lng, activeSector.bearing);
                        const shareText = `${building?.name || 'Emplazamiento'} · ${activeSector.compass} ${activeSector.bearing}° · Street View: ${url}`;
                        try {
                          if (navigator.share) {
                            await navigator.share({ title: 'Vista panorámica', text: shareText, url });
                            toast.success('Compartido');
                          } else {
                            await navigator.clipboard.writeText(url);
                            toast.success('Enlace Street View copiado al portapapeles');
                          }
                        } catch (e) {
                          try {
                            await navigator.clipboard.writeText(url);
                            toast.success('Enlace copiado');
                          } catch (_) {
                            toast.error('No se pudo copiar el enlace');
                          }
                        }
                      }}
                      data-testid="btn-share-streetview"
                      className="bg-[#A3FF12]/15 hover:bg-[#A3FF12]/25 border border-[#A3FF12]/40 text-[#A3FF12] text-[11px] font-bold px-3 py-1.5 rounded-lg flex items-center gap-1"
                      title="Copiar enlace de esta vista para mandársela a alguien"
                    >
                      <Share2 size={12}/> Compartir vista
                    </button>
                    <a href={streetViewOpen(building.lat, building.lng, activeSector.bearing)} target="_blank" rel="noreferrer"
                       data-testid="btn-open-streetview"
                       className="bg-blue-500/20 hover:bg-blue-500/30 border border-blue-500/40 text-blue-200 text-[11px] font-bold px-3 py-1.5 rounded-lg flex items-center gap-1">
                      <ExternalLink size={12}/> Abrir en Google Maps
                    </a>
                    </div>
                  </div>
                  <div className="relative flex-1 min-h-[340px]">
                    <iframe
                      key={`${building.lat}-${building.lng}-${activeSector.bearing}`}
                      src={streetViewEmbed(building.lat, building.lng, activeSector.bearing)}
                      title={`Street View ${activeSector.compass} ${activeSector.bearing}°`}
                      className="w-full h-full border-0 block"
                      style={{ pointerEvents: ivInteractive ? 'auto' : 'none' }}
                      allow="fullscreen"
                      loading="lazy"
                      data-testid="streetview-iframe"
                    />
                    {!ivInteractive && (
                      <button
                        type="button"
                        onClick={() => setIvInteractive(true)}
                        data-testid="streetview-activate"
                        className="absolute inset-0 flex items-end justify-center pb-4 group cursor-pointer"
                      >
                        <div className="bg-black/70 backdrop-blur border border-white/25 rounded-full px-4 py-2 text-white text-xs font-bold shadow-lg group-hover:bg-black/90 flex items-center gap-2">
                          <Compass size={13} className="text-blue-300"/>
                          Clic para navegar por Street View
                        </div>
                      </button>
                    )}
                    {ivInteractive && (
                      <button
                        type="button"
                        onClick={() => setIvInteractive(false)}
                        data-testid="streetview-release"
                        className="absolute top-2 right-2 bg-black/70 backdrop-blur border border-white/25 rounded-full px-2.5 py-1 text-white text-[10px] font-bold hover:bg-black/90"
                        title="Bloquear iframe para poder hacer scroll en la ventana"
                      >
                        Liberar scroll
                      </button>
                    )}
                  </div>
                </>
              ) : (
                <div className="flex-1 flex items-center justify-center text-white/40 text-sm p-8 text-center">
                  <div>
                    <Compass className="mx-auto mb-2 opacity-30" size={48}/>
                    <div className="font-bold text-white/60 mb-1">Sin sector seleccionado</div>
                    <div>Pulsa cualquier sector del radar para ver la vista Street View real en esa dirección.</div>
                  </div>
                </div>
              )}
            </div>

            {/* Top obstáculos (full width bajo el grid) */}
            <div className="lg:col-span-2 bg-black/40 border border-white/10 rounded-2xl p-4">
              <div className="text-white/70 text-xs font-bold uppercase mb-2">Top obstáculos</div>
              <div className="grid sm:grid-cols-2 gap-1.5 text-xs">
                {[...sectors].map((s, i) => ({ s, i })).filter((x) => x.s.is_obstacle).sort((a, b) => (b.s.elevation_deg || 0) - (a.s.elevation_deg || 0)).slice(0, 10).map(({ s, i }) => (
                  <button key={i} onClick={() => setSelected(i)}
                    className={`flex items-center justify-between gap-2 rounded-lg px-2.5 py-1.5 text-left transition-colors ${selected === i ? 'bg-blue-500/20 border-blue-500/40 border' : 'bg-black/40 border border-white/5 hover:border-blue-500/30'}`}>
                    <span className="font-mono text-blue-300 w-14">{s.compass} · {s.bearing}°</span>
                    <span className="flex-1 text-white/80 truncate">{s.obstacle_name || 'sin nombre'} {s.obstacle_type && <span className="text-white/40">({s.obstacle_type})</span>}</span>
                    <span className="text-red-300 font-bold">{s.obstacle_height_m}m</span>
                    <span className="text-white/50">↑{s.elevation_deg}°</span>
                  </button>
                ))}
                {sectors.filter((s) => s.is_obstacle).length === 0 && (
                  <div className="text-[#A3FF12] font-bold text-center py-3 col-span-2">✓ Cobertura 360° totalmente libre</div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AreaAnalysisPanel;
